// SELECTOR DE PAÍS: el `Select` del catálogo con la bandera de cada país (operador, 5-oct-2026).
//
// QUÉ PASABA. El `Select` sabe pintar un ícono por opción, pero la bandera la ponía CADA
// pantalla al armar su lista: solo Registrar caso lo hacía, y los filtros de país de las
// bandejas, Indicadores, Disposiciones y Destinatarios salían sin bandera. Ahora todo selector
// de país es este componente: la opción (bandera, nombre y, si se pide, la autoridad) se arma
// en un solo sitio y cambiarla aquí la cambia en todas las pantallas.
//
// LOS PAÍSES llegan de dos formas: del catálogo (`cod`) o de la respuesta de una bandeja
// (`codigo`). Se aceptan las dos para que ninguna pantalla tenga que traducir.
import type { ComponentProps } from 'react';
import Bandera from '../Banderas';
import { Select } from '../ui';
import type { OpcionDeSelect } from '../ui';

/** Un país tal como lo trae el catálogo (`cod`) o una bandeja (`codigo`). */
export type PaisParaElegir = { cod?: string; codigo?: string; nombre: string; autoridad?: string; pistaDeAutoridades?: string };

const codigoDe = (p: PaisParaElegir): string => p.cod ?? p.codigo ?? '';

/**
 * Las opciones de país con su bandera. `conAutoridad` pone la autoridad sanitaria como segunda
 * línea (la que el `Select` pinta en gris), sin mezclarla con el nombre.
 */
export function opcionesDePais(paises: PaisParaElegir[], { conAutoridad = false }: { conAutoridad?: boolean } = {}): OpcionDeSelect[] {
  return paises.filter((p) => codigoDe(p)).map((p) => ({
    value: codigoDe(p),
    label: p.nombre,
    // CC2-01-C1: las autoridades activas del país («SENASA · SANIPES»); si no vienen, la predeterminada.
    hint: conAutoridad ? (p.pistaDeAutoridades || p.autoridad || undefined) : undefined,
    // Decorativa: el nombre del país ya es la etiqueta. Con su `role="img"` el lector diría
    // «Bandera de Perú Perú» y el nombre accesible de la opción dejaría de ser «Perú».
    icon: <span aria-hidden="true" className="flex shrink-0"><Bandera cod={codigoDe(p)} nombre={p.nombre} /></span>,
  }));
}

export type PropsDeSelectorDePais = Omit<ComponentProps<typeof Select>, 'options'> & {
  paises: PaisParaElegir[];
  /** La autoridad del país como segunda línea de cada opción. */
  conAutoridad?: boolean;
  /** Opciones que van ANTES de los países, p. ej. «Todos los países» cuando es un valor elegible. */
  antes?: OpcionDeSelect[];
};

export default function SelectorDePais({ paises, conAutoridad = false, antes = [], ...propsDelSelect }: PropsDeSelectorDePais) {
  return <Select {...propsDelSelect} options={[...antes, ...opcionesDePais(paises, { conAutoridad })]} />;
}
